/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Inline Action Alert styles
 *
 * @file inlineActionAlertStyles.js
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import { styled } from '@mui/material/styles';
import mixins from '../../assets/styles/mixins.scss';
import { createScopedClasses, createScopedStyles } from '../../assets/styles/createScopedStyles';

/** Left highlight bar — student temp access warning banner (feedbackVariant: warning). */
const feedbackWarningAccent = '#EA7600';
/** Left highlight bar — student temp access alert banner (feedbackVariant: alert). */
const feedbackNegativeAccent = mixins.accentRedDark;
/** BannerWarningIcon color — student temp access warning banner. Applied via feedbackIconWarning. */
const feedbackWarningIconColor = '#EA7600';
/** AlertIcon color — student temp access alert banner. Applied via feedbackIconAlert. */
const feedbackAlertIconColor = mixins.accentRedDark;
const inlineActionAlertStyles = {
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 16,
    backgroundColor: mixins.lightThemeBgColor2,
    width: '100%',
    boxSizing: 'border-box',
    flexWrap: 'wrap',

    [mixins['md-maxwidth']]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 8
    }
  },
  rootFeedback: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 8,
    padding: '2px 8px 2px 2px',
    borderRadius: 4,
    backgroundColor: mixins.feedBackColor,
    width: '100%',
    boxSizing: 'border-box'
  },
  highlight: {
    width: 3,
    flexShrink: 0,
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4
  },
  /** Left highlight bar — student temp access warning banner (feedbackVariant: warning). */
  highlightWarning: {
    backgroundColor: feedbackWarningAccent
  },
  /** Left highlight bar — student temp access alert banner (feedbackVariant: alert). */
  highlightAlert: {
    backgroundColor: feedbackNegativeAccent
  },
  feedbackContent: {
    position: 'relative',
    display: 'flex',
    flex: '1 1 0',
    minWidth: 0,
    alignItems: 'center',
    gap: 12,
    padding: '8px 0',

    [mixins['md-sm-maxwidth']]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 16
    }
  },
  feedbackContentWithClose: {
    [mixins['md-sm-maxwidth']]: {
      paddingRight: 26
    }
  },
  feedbackMain: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    paddingRight: 8,

    [mixins['md-sm-maxwidth']]: {
      width: '100%',
      paddingRight: 0
    }
  },
  messageLeading: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: '1 1 auto',
    minWidth: 0,

    [mixins['md-sm-maxwidth']]: {
      alignItems: 'flex-start',
      width: '100%',

      '& > div:first-child': {
        marginTop: 4
      }
    }
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 16,
    height: 16,
    lineHeight: 0
  },
  /** Base 16px icon sizing for all InlineActionAlert banner icons (MUI SvgIcon needs fontSize). */
  feedbackIcon: {
    width: 16,
    height: 16,
    fontSize: 16,
    display: 'block',
    flexShrink: 0,
    '& svg': {
      display: 'block'
    }
  },
  /** BannerWarningIcon — student temp access warning banner (feedbackVariant: warning). */
  feedbackIconWarning: {
    color: feedbackWarningIconColor
  },
  /** AlertIcon — student temp access alert banner (feedbackVariant: alert). */
  feedbackIconAlert: {
    color: feedbackAlertIconColor
  },
  /** BannerCloseIcon — dismiss control on student temp access warning/alert banners. */
  closeButton: {
    flexShrink: 0,
    alignSelf: 'flex-start',
    padding: 0,
    width: 18,
    height: 18,
    minWidth: 18,
    borderRadius: '50%',
    color: mixins.color_text_primary,
    transition: 'background-color 150ms ease-in-out',

    [mixins['md-sm-maxwidth']]: {
      position: 'absolute',
      top: 8,
      right: 0
    },

    '&:hover': {
      backgroundColor: mixins.flaggedChipColor,
      color: mixins.color_text_primary
    },
    '&:focus-visible': {
      outline: `2px solid ${mixins.navyColor}`,
      outlineOffset: 2
    }
  },
  /** BannerCloseIcon sizing — student temp access warning/alert banners. */
  closeIcon: {
    width: 18,
    height: 18
  },
  /** When no CTA is shown (e.g. temp access warning with show: false), center the dismiss control. */
  closeButtonNoCta: {
    alignSelf: 'center',

    [mixins['md-sm-maxwidth']]: {
      top: '50%',
      transform: 'translateY(-50%)'
    }
  },
  content: {
    display: 'flex',
    flex: '1 1 0',
    minWidth: 0,
    gap: 12,
    alignItems: 'flex-start',

    [mixins['md-maxwidth']]: {
      flex: 'none',
      width: '100%'
    }
  },
  accent: {
    width: 4,
    alignSelf: 'stretch',
    borderRadius: 8,
    flexShrink: 0
  },
  message: {
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    lineHeight: '24px',
    color: mixins.lightTextColor2,
    fontFamily: mixins.fontFamilyPlusSans,
    padding: '4px 0',
    flex: '1 1 0',
    minWidth: 0,
    wordBreak: 'break-word'
  },
  messageFeedback: {
    padding: 0,
    margin: 0,
    flex: '1 1 auto',
    minWidth: 0,
    lineHeight: '24px'
  },
  actions: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexShrink: 0,
    flexWrap: 'wrap',

    [mixins['md-maxwidth']]: {
      width: '100%',
      flexDirection: 'column',
      alignItems: 'stretch',

      '& > span': {
        display: 'block',
        width: '100%'
      }
    }
  },
  actionsInline: {
    display: 'flex',
    gap: 18,
    alignItems: 'center',
    flexShrink: 0,
    flexWrap: 'nowrap',

    [mixins['md-maxwidth']]: {
      width: 'auto',
      flexDirection: 'row',
      alignItems: 'center',

      '& > span': {
        display: 'inline',
        width: 'auto'
      }
    },

    [mixins['md-sm-maxwidth']]: {
      width: '100%',
      flexDirection: 'column',
      alignItems: 'stretch',

      '& > span': {
        display: 'block',
        width: '100%'
      }
    }
  },
  secondaryButton: {
    borderRadius: 1000,
    border: `2px solid ${mixins.lightTextColor2}`,
    color: mixins.lightTextColor2,
    padding: '12px 20px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    textTransform: 'none',
    backgroundColor: 'transparent',
    minHeight: 40,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: mixins.flaggedChipColor
    },
    '&:disabled': {
      borderColor: mixins.disabledInputBoxColor,
      color: mixins.disabledInputBoxColor
    },

    [mixins['md-maxwidth']]: {
      width: '100%'
    },

    [mixins['md-sm-maxwidth']]: {
      width: '100%'
    },

    '&.MuiButton-root, &.MuiButton-text': {
      '&:hover': {
        color: mixins.lightTextColor2,
        border: `2px solid ${mixins.lightTextColor2}`
      }
    }
  },
  primaryButton: {
    borderRadius: 1000,
    backgroundColor: mixins.amethyst,
    color: mixins.chalkWhiteFont,
    padding: '12px 20px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    textTransform: 'none',
    minHeight: 40,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: mixins.amethystColorV2,
      color: mixins.chalkWhiteFont
    },
    '&:disabled': {
      backgroundColor: mixins.bulkEditCtaButtonDisabledBGColor,
      color: '#8B8B8B'
    },

    [mixins['md-maxwidth']]: {
      width: '100%'
    },

    [mixins['md-sm-maxwidth']]: {
      width: '100%'
    },

    '&.MuiButton-root, &.MuiButton-contained': {
      border: 'none',
      '&:hover': {
        border: 'none'
      }
    }
  }
};

export const classes = createScopedClasses('aet-inline-action-alert', inlineActionAlertStyles);

export const scopedStyles = createScopedStyles(inlineActionAlertStyles, classes);

export const InlineActionAlertStylesRoot = styled('div')(scopedStyles);

export default inlineActionAlertStyles;
